import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Field,
  Header,
  HeaderIconButton,
  HeaderText,
  Input,
  Message,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import useBackNavigation from '../utils/useBackNavigation';

const Form = styled.form`
  display: grid;
  gap: 1rem;
`;

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const formatDateInputValue = (dateValue) => {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const isWithinEditWindow = (alarm) => {
  const createdAt = new Date(alarm.createdAt).getTime();

  if (Number.isNaN(createdAt)) {
    return false;
  }

  return Date.now() - createdAt <= 3 * 60 * 1000;
};

const EditAlarm = () => {
  const { user } = useAuth();
  const { alarmId } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/manage-pledges');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAlarm = async () => {
      try {
        const response = await axios.get(`${window.RUNTIME_CONFIG.apiUrl}/alarms`, {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        });
        const alarm = response.data.find(alarmItem => alarmItem.id === alarmId);

        if (!alarm) {
          setError('Pledge not found');
          setLoading(false);
          return;
        }

        if (!alarm.active || !isWithinEditWindow(alarm)) {
          setError('Pledges can only be edited within 3 minutes of creation');
          setLoading(false);
          return;
        }

        const alarmTime = new Date(alarm.time);
        setDate(formatDateInputValue(alarmTime));
        setTime(alarmTime.toTimeString().slice(0, 5));
        setLoading(false);
      } catch (error) {
        console.error('Error fetching alarm:', error);
        setError('Failed to fetch pledge');
        setLoading(false);
      }
    };

    fetchAlarm();
  }, [alarmId, user]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);

    try {
      await axios.put(
        `${window.RUNTIME_CONFIG.apiUrl}/alarms/${alarmId}`,
        {
          time: new Date(`${date}T${time}`).toISOString()
        },
        {
          headers: { Authorization: `Bearer ${await user.getIdToken()}` }
        }
      );

      navigate('/manage-pledges');
    } catch (error) {
      console.error('Error updating alarm:', error);
      setError(error.response?.data?.message || 'Failed to update pledge');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <Shell>
          <Card>Loading pledge...</Card>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Edit pledge.</Title>
            <Subtitle>You can adjust the time briefly after creating it. Tiny mercy, strong boundaries.</Subtitle>
          </HeaderText>
        </Header>

        <Card>
          <Form onSubmit={handleSubmit}>
            <InputGroup>
              <Field>
                Date
                <Input
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  required
                />
              </Field>
              <Field>
                Time
                <Input
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value)}
                  required
                />
              </Field>
            </InputGroup>

            {error && <Message $error>{error}</Message>}

            <Actions>
              <Button type="submit" disabled={saving || !date || !time || Boolean(error)}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Actions>
          </Form>
        </Card>
      </Shell>
    </Page>
  );
};

export default EditAlarm;
